<template>
    <div>
        <div id="Dashboard" class="container is-fluid">
            <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />

            <!-- Header -->
            <HeroContainer title="Sales Dashboard" />

            <section id="summary_filters" class="filters-section">
                <div class="card shadow-sm filters-card">
                    <div class="card-body filters-body">
                        <div class="fields filters-grid">
                            <div class="field">
                                <input
                                    id="switch_to_date"
                                    type="checkbox"
                                    name="switch_to_date"
                                    class="switch is-rounded is-info"
                                    checked="checked"
                                    v-model="to_date"
                                />
                                <label
                                    for="switch_to_date"
                                    style="outline: none; margin-right: 0.5rem; min-width"
                                >
                                    <span v-if="to_date">To date</span>
                                    <span v-else>All dates</span>
                                </label>
                            </div>
                            <div class="select">
                                <select id="customer_select" v-model="filter_customer">
                                    <option value="">All Customers</option>
                                    <option
                                        v-for="item in customer_items"
                                        :key="'cust_' + item"
                                        :value="item"
                                    >
                                        {{ item }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="buttons is-right filters-actions">
                            <div class="filter-action">
                                <button
                                    id="filter_button"
                                    class="button is-info"
                                    type="button"
                                    @click="filter_summary_records"
                                    :disabled="isSearching"
                                >
                                    Filter
                                </button>
                                <span
                                    v-if="isSearching"
                                    class="loading-icon"
                                    aria-live="polite"
                                    aria-label="Loading summary"
                                >
                                    <i class="material-icons rotating">.</i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section style="display: flex; flex-direction: row; justify-content: center">
                <div
                    id="row_labels_left"
                    style="
                        flex: 0 5 0;
                        display: flex;
                        flex-direction: column;
                        justify-content: space-around;
                    "
                >
                    <div>Profit</div>
                    <div>Sales</div>
                    <div>Quotes</div>
                </div>

                <div
                    id="Day"
                    style="display: flex; flex-direction: column; justify-content: space-evenly"
                >
                    <div class="column_label_top">Day over Day</div>
                    <div class="card">
                        <div class="card-content">
                            <div class="total" v-bind:class="{ small_num: is_down_p_day }">
                                {{ summary.profit_total_today }}
                            </div>
                            <div
                                class="percent_diff"
                                v-bind:class="[
                                    is_down_p_day ? 'has-text-danger' : '',
                                    'has-text-success',
                                ]"
                            >
                                <i v-if="is_down_p_day" class="material-icons"> arrow_drop_down </i>
                                <i v-else class="material-icons"> arrow_drop_up </i>
                                {{ summary.diff_profit_day }}
                            </div>
                            <div class="total" v-bind:class="{ small_num: !is_down_p_day }">
                                {{ summary.profit_total_yesterday }}
                            </div>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-content">
                            <div class="total" v-bind:class="{ small_num: is_down_s_day }">
                                {{ summary.sales_total_today }}
                            </div>
                            <div
                                class="percent_diff"
                                v-bind:class="[
                                    is_down_s_day ? 'has-text-danger' : '',
                                    'has-text-success',
                                ]"
                            >
                                <i v-if="is_down_s_day" class="material-icons"> arrow_drop_down </i>
                                <i v-else class="material-icons"> arrow_drop_up </i>
                                {{ summary.diff_sales_day }}
                            </div>
                            <div class="total" v-bind:class="{ small_num: !is_down_s_day }">
                                {{ summary.sales_total_yesterday }}
                            </div>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-content">
                            <div class="total" v-bind:class="{ small_num: is_down_q_day }">
                                {{ summary.quote_total_today }}
                            </div>
                            <div
                                class="percent_diff"
                                v-bind:class="[
                                    is_down_q_day ? 'has-text-danger' : '',
                                    'has-text-success',
                                ]"
                            >
                                <i v-if="is_down_q_day" class="material-icons"> arrow_drop_down </i>
                                <i v-else class="material-icons"> arrow_drop_up </i>
                                {{ summary.diff_quotes_day }}
                            </div>
                            <div class="total" v-bind:class="{ small_num: !is_down_q_day }">
                                {{ summary.quote_total_yesterday }}
                            </div>
                        </div>
                    </div>
                    <div class="column_label_bottom">Day over Day</div>
                </div>

                <div
                    id="Month"
                    style="
                        display: flex;
                        flex-direction: column;
                        justify-content: space-evenly;
                        margin: 0 4rem;
                    "
                >
                    <div class="column_label_top">Month over Month</div>
                    <div class="card">
                        <div class="card-content">
                            <div class="total" v-bind:class="{ small_num: is_down_p_month }">
                                {{ summary.profit_total_month }}
                            </div>
                            <div
                                class="percent_diff"
                                v-bind:class="[
                                    is_down_p_month ? 'has-text-danger' : '',
                                    'has-text-success',
                                ]"
                            >
                                <i v-if="is_down_p_month" class="material-icons">
                                    arrow_drop_down
                                </i>
                                <i v-else class="material-icons"> arrow_drop_up </i>
                                {{ summary.diff_profit_month }}
                            </div>
                            <div class="total" v-bind:class="{ small_num: !is_down_p_month }">
                                {{ summary.profit_total_month_last }}
                            </div>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-content">
                            <div class="total" v-bind:class="{ small_num: is_down_s_month }">
                                {{ summary.sales_total_month }}
                            </div>
                            <div
                                class="percent_diff"
                                v-bind:class="[
                                    is_down_s_month ? 'has-text-danger' : '',
                                    'has-text-success',
                                ]"
                            >
                                <i v-if="is_down_s_month" class="material-icons">
                                    arrow_drop_down
                                </i>
                                <i v-else class="material-icons"> arrow_drop_up </i>
                                {{ summary.diff_sales_month }}
                            </div>
                            <div class="total" v-bind:class="{ small_num: !is_down_s_month }">
                                {{ summary.sales_total_month_last }}
                            </div>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-content">
                            <div class="total" v-bind:class="{ small_num: is_down_q_month }">
                                {{ summary.quote_total_month }}
                            </div>
                            <div
                                class="percent_diff"
                                v-bind:class="[
                                    is_down_q_month ? 'has-text-danger' : '',
                                    'has-text-success',
                                ]"
                            >
                                <i v-if="is_down_q_month" class="material-icons">
                                    arrow_drop_down
                                </i>
                                <i v-else class="material-icons"> arrow_drop_up </i>
                                {{ summary.diff_quotes_month }}
                            </div>
                            <div class="total" v-bind:class="{ small_num: !is_down_q_month }">
                                {{ summary.quote_total_month_last }}
                            </div>
                        </div>
                    </div>
                    <div class="column_label_bottom">Month over Month</div>
                </div>

                <div
                    id="Year"
                    style="display: flex; flex-direction: column; justify-content: space-evenly"
                >
                    <div class="column_label_top">Year over Year</div>
                    <div class="card">
                        <div class="card-content">
                            <div class="total" v-bind:class="{ small_num: is_down_p_year }">
                                {{ summary.profit_total_year }}
                            </div>
                            <div
                                class="percent_diff"
                                v-bind:class="[
                                    is_down_p_year ? 'has-text-danger' : '',
                                    'has-text-success',
                                ]"
                            >
                                <i v-if="is_down_p_year" class="material-icons">
                                    arrow_drop_down
                                </i>
                                <i v-else class="material-icons"> arrow_drop_up </i>
                                {{ summary.diff_profit_year }}
                            </div>
                            <div class="total" v-bind:class="{ small_num: !is_down_p_year }">
                                {{ summary.profit_total_year_last }}
                            </div>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-content">
                            <div class="total" v-bind:class="{ small_num: is_down_s_year }">
                                {{ summary.sales_total_year }}
                            </div>
                            <div
                                class="percent_diff"
                                v-bind:class="[
                                    is_down_s_year ? 'has-text-danger' : '',
                                    'has-text-success',
                                ]"
                            >
                                <i v-if="is_down_s_year" class="material-icons">
                                    arrow_drop_down
                                </i>
                                <i v-else class="material-icons"> arrow_drop_up </i>
                                {{ summary.diff_sales_year }}
                            </div>
                            <div class="total" v-bind:class="{ small_num: !is_down_s_year }">
                                {{ summary.sales_total_year_last }}
                            </div>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-content">
                            <div class="total" v-bind:class="{ small_num: is_down_q_year }">
                                {{ summary.quote_total_year }}
                            </div>
                            <div
                                class="percent_diff"
                                v-bind:class="[
                                    is_down_q_year ? 'has-text-danger' : '',
                                    'has-text-success',
                                ]"
                            >
                                <i v-if="is_down_q_year" class="material-icons">
                                    arrow_drop_down
                                </i>
                                <i v-else class="material-icons"> arrow_drop_up </i>
                                {{ summary.diff_quotes_year }}
                            </div>
                            <div class="total" v-bind:class="{ small_num: !is_down_q_year }">
                                {{ summary.quote_total_year_last }}
                            </div>
                        </div>
                    </div>
                    <div class="column_label_bottom">Year over Year</div>
                </div>

                <div
                    id="row_labels_right"
                    style="
                        flex: 0 5 0;
                        display: flex;
                        flex-direction: column;
                        justify-content: space-around;
                    "
                >
                    <div>Profit</div>
                    <div>Sales</div>
                    <div>Quotes</div>
                </div>
            </section>

            <div class="is-divider" data-content="OR"></div>
            <hr style="margin: 5rem 5rem" />

            <section id="chart_filters" class="filters-section">
                <div class="card shadow-sm filters-card">
                    <div class="card-body filters-body">
                        <div class="field is-horizontal">
                            <div class="field-label is-normal">
                                <label class="label">Group by</label>
                            </div>
                            <div class="field-body">
                                <div class="field">
                                    <span class="select">
                                        <select id="period" v-model="period">
                                            <option disabled hidden value="" style="color: grey">
                                                Period
                                            </option>
                                            <option value="day">Day</option>
                                            <option value="week">Week</option>
                                            <option value="month">Month</option>
                                            <option value="quarter">Quarter</option>
                                        </select>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="field is-horizontal">
                            <div class="field-label is-normal">
                                <label class="label">Period</label>
                            </div>
                            <div class="field-body">
                                <div class="field has-addons">
                                    <p class="control">
                                        <span class="select">
                                            <select
                                                id="date_range"
                                                v-model="date_range"
                                                @change="change_date(date_range)"
                                            >
                                                <option
                                                    disabled
                                                    hidden
                                                    value=""
                                                    style="color: grey"
                                                >
                                                    Date Presets
                                                </option>
                                                <option value="today">Today</option>
                                                <option value="week">Week</option>
                                                <option value="this_month">This Month</option>
                                                <option value="prev_month">Prev Month</option>
                                                <option value="this_quarter">This Quarter</option>
                                                <option value="prev_quarter">Prev Quarter</option>
                                                <option value="this_year">This Year</option>
                                                <option value="prev_year">Prev Year</option>
                                                <option value="all">All</option>
                                                <option disabled hidden value="custom">
                                                    Custom
                                                </option>
                                            </select>
                                        </span>
                                    </p>
                                    <p class="control is-expanded">
                                        <VueDatePicker
                                            v-model="range"
                                            :enable-time-picker="false"
                                            range
                                        />
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="buttons is-right filters-actions">
                            <button @click="reset" class="button is-small is-white">Reset</button>
                            <div class="filter-action">
                                <button
                                    @click="loadChart"
                                    class="button is-info"
                                    :disabled="isSearching"
                                >
                                    Filter
                                </button>
                                <span
                                    v-if="isSearching"
                                    class="loading-icon"
                                    aria-live="polite"
                                    aria-label="Loading chart"
                                >
                                    <i class="material-icons rotating">.</i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="plot_data_bar_line" class="chart-section">
                <div class="card shadow-sm">
                    <div class="card-body">
                        <PlotlyFigure
                            :plot_data="plotDataBarLine"
                            :layout="layout_barline"
                            :options="options"
                            :key="figureKey_BarLine"
                        >
                        </PlotlyFigure>
                    </div>
                </div>
            </section>

            <section id="plot_data_revenue" class="chart-section">
                <div class="card shadow-sm">
                    <div class="card-body">
                        <PlotlyFigure
                            :plot_data="plotDataRevenue"
                            :layout="layout_revenue"
                            :options="options"
                            :key="figureKey_Revenue"
                        >
                        </PlotlyFigure>
                    </div>
                </div>
            </section>

            <section id="plot_data_profit" class="chart-section">
                <div class="card shadow-sm">
                    <div class="card-body">
                        <PlotlyFigure
                            :plot_data="plotDataProfit"
                            :layout="layout_profit"
                            :options="options"
                            :key="figureKey_Profit"
                        >
                        </PlotlyFigure>
                    </div>
                </div>
            </section>

            <section id="plot_data_po" class="chart-section">
                <div class="card shadow-sm">
                    <div class="card-body">
                        <PlotlyFigure
                            :plot_data="plotDataPO"
                            :layout="layout_po"
                            :options="options"
                            :key="figureKey_PO"
                        >
                        </PlotlyFigure>
                    </div>
                </div>
            </section>

            <section id="dashboard_data" v-if="queryData.data.length > 0">
                <DataTable2
                    :raw_data="queryData"
                    :name="'dash_sales_detail'"
                    :columns_from_parent="[
                        {
                            label: 'Part',
                            field: 'part_number',
                            type: 'text',
                        },
                        {
                            label: 'Description',
                            field: 'description',
                            type: 'text',
                        },
                        {
                            label: 'Invoice',
                            field: 'invoice',
                            type: 'text',
                        },
                        {
                            label: 'Margin',
                            field: 'margin',
                            type: 'number',
                            formatFn: function (value) {
                                return numeral(value).format('0,0,0,0,0.00%')
                            },
                        },
                        {
                            label: 'Company PO',
                            field: 'company_po_number',
                            type: 'text',
                        },
                        {
                            label: 'Profit',
                            field: 'profit',
                            type: 'number',
                            formatFn: function (value) {
                                return numeral(value).format('$0,0,0,0,0.00')
                            },
                        },
                        {
                            label: 'Unit Cost',
                            field: 'unit_cost',
                            type: 'number',
                            formatFn: function (value) {
                                return numeral(value).format('$0,0,0,0,0.00')
                            },
                        },
                        {
                            label: 'Unit Price',
                            field: 'unit_price',
                            type: 'number',
                            formatFn: function (value) {
                                return numeral(value).format('$0,0,0,0,0.00')
                            },
                        },
                        {
                            label: 'Total Cost',
                            field: 'total_cost',
                            type: 'number',
                            formatFn: function (value) {
                                return numeral(value).format('$0,0,0,0,0.00')
                            },
                        },
                        {
                            label: 'Total Price',
                            field: 'total_price',
                            type: 'number',
                            formatFn: function (value) {
                                return numeral(value).format('$0,0,0,0,0.00')
                            },
                        },
                        {
                            label: 'Bill Name',
                            field: 'bill_name',
                            type: 'text',
                        },
                        {
                            label: 'Vendor',
                            field: 'vendor_name',
                            type: 'text',
                        },
                        {
                            label: 'Dept Name',
                            field: 'dept_name',
                            type: 'text',
                        },
                        {
                            label: 'Orig PO',
                            field: 'original_po_number',
                            type: 'text',
                        },
                        {
                            label: 'Qty Invoiced',
                            field: 'qty_invoiced',
                            type: 'number',
                            formatFn: function (value) {
                                return numeral(value).format('0,0,0,0,0')
                            },
                        },
                        {
                            label: 'Reference',
                            field: 'reference',
                            type: 'text',
                        },
                        {
                            label: 'Invoice Date',
                            field: 'invoice_date',
                            type: 'text',
                            formatFn: function (value) {
                                return value.split('T')[0]
                            },
                        },
                        {
                            label: 'Post Date',
                            field: 'post_date',
                            type: 'text',
                            formatFn: function (value) {
                                return value.split('T')[0]
                            },
                        },
                        {
                            label: 'Time Created',
                            field: 'time_created',
                            type: 'text',
                            formatFn: function (value) {
                                return value.split('T')[0]
                            },
                        },
                    ]"
                />
                <div class="buttons is-right">
                    <button
                        class="button is-info"
                        style="margin: 0.5em 0 0 0"
                        @click="
                            $emit('download-csv', [queryData.data, 'SalesChartDashboard', null])
                        "
                    >
                        Download CSV
                    </button>
                </div>
            </section>
        </div>
    </div>
</template>

<style scoped>
    #row_labels_right > div,
    .column_label_bottom {
        color: slategray;
        font-size: 1.25rem;
        font-weight: 400;
        line-height: 1.25;
        text-align: center;
    }
    #row_labels_left > div {
        color: #363636;
        font-size: 2rem;
        font-weight: 600;
        line-height: 1.125;
        text-align: center;
    }
    .column_label_top {
        color: #363636;
        font-size: 1.7rem;
        font-weight: 600;
        line-height: 1.125;
        text-align: center;
    }
    #row_labels_right > div,
    #row_labels_left > div {
        transform: rotate(-90deg);
    }
    .card {
        margin: 1rem 0;
    }
    .total {
        font-size: 2.5rem;
    }
    .percent_diff {
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .small_num {
        color: slategray;
    }
    .filters-section {
        display: flex;
        justify-content: center;
        margin-bottom: 2rem;
    }
    .filters-card {
        width: 100%;
        max-width: 960px;
    }
    .filters-body {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }
    .filters-grid {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
        align-items: center;
    }
    .filters-actions {
        gap: 0.75rem;
    }
    .filter-action {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }
    .loading-icon {
        display: flex;
        align-items: center;
        color: #3273dc;
    }
    .rotating {
        animation: spin 1s linear infinite;
    }
    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }
</style>

<script>
    import { mapStores } from 'pinia'
    import DataTable2 from '@/components/shared/DataTable2.vue'
    import PlotlyFigure from '@/components/shared/Plotly.vue'
    import moment from 'moment'
    import DataFrame from 'dataframe-js'
    import VueDatePicker from '@vuepic/vue-datepicker'
    import { getDates } from '@/helpers/dates.js'
    import HeroContainer from '@/components/shared/HeroContainer.vue'
    import { useDropdownsStore } from '@/stores/dropdownsStore'
    import { useDashboardSalesChartStore } from '@/stores/dashboardSalesChartStore'
    import { useSearchStore } from '@/stores/searchStore'

    export default {
        name: 'DashboardSalesChart',
        components: {
            DataTable2,
            PlotlyFigure,
            VueDatePicker,
            HeroContainer,
        },
        computed: {
            ...mapStores(useDropdownsStore, useDashboardSalesChartStore, useSearchStore),
            isSearching() {
                return this.searchStore.ajax_searching > 0 || this.dashboardSalesChartStore.loading
            },
            summary() {
                return this.dashboardSalesChartStore.summary
            },
            customer_items() {
                return this.dropdownsStore.customer
            },
            is_down_p_day: function () {
                return parseFloat(this.summary.diff_profit_day) < 0.0
            },
            is_down_p_month: function () {
                return parseFloat(this.summary.diff_profit_month) < 0.0
            },
            is_down_p_year: function () {
                return parseFloat(this.summary.diff_profit_year) < 0.0
            },
            is_down_q_day: function () {
                return parseFloat(this.summary.diff_quotes_day) < 0.0
            },
            is_down_q_month: function () {
                return parseFloat(this.summary.diff_quotes_month) < 0.0
            },
            is_down_q_year: function () {
                return parseFloat(this.summary.diff_quotes_year) < 0.0
            },
            is_down_s_day: function () {
                return parseFloat(this.summary.diff_sales_day) < 0.0
            },
            is_down_s_month: function () {
                return parseFloat(this.summary.diff_sales_month) < 0.0
            },
            is_down_s_year: function () {
                return parseFloat(this.summary.diff_sales_year) < 0.0
            },
            queryData() {
                let raw_query = [...this.dashboardSalesChartStore.queryDataRaw]
                if (Object.keys(raw_query).length == 0) {
                    return { site: '', data: [] }
                }
                return { site: '', data: raw_query }
            },
            color_dict() {
                let colors = {}
                if (
                    Object.prototype.hasOwnProperty.call(
                        this.dashboardSalesChartStore.plotDataRaw,
                        'sales'
                    )
                ) {
                    let sales_data = this.dashboardSalesChartStore.plotDataRaw.sales
                    let df = new DataFrame(sales_data)
                    let groups = df.groupBy('customer')
                    let agg_rows = groups
                        .aggregate((group) => group.stat.sum('price'))
                        .rename('aggregation', 'price')
                        .sortBy('price', true)
                        .toCollection()
                    for (let i in agg_rows) {
                        let mod = i % 10
                        let customer = this.get_short_name(agg_rows[i].customer)
                        colors[customer] = this.color_list[mod]
                    }
                }
                return colors
            },
            plotDataPO() {
                let data_dict = {}

                let data = []
                if (
                    Object.prototype.hasOwnProperty.call(
                        this.dashboardSalesChartStore.plotDataRaw,
                        'sales'
                    )
                ) {
                    let sales_data = this.dashboardSalesChartStore.plotDataRaw.sales
                    if (sales_data.length > 0) {
                        let df = new DataFrame(sales_data)
                        let periods = df.unique('period').toDict().period
                        periods.sort()
                        let this_week = periods.slice(-1)
                        var data_this_week = {
                            type: 'pie',
                            name: 'This Week',
                            values: [],
                            labels: [],
                            textinfo: 'label+value',
                            xaxis: 'x2',
                            yaxis: 'y2',
                            textposition: 'inside',
                            hole: 0.4,
                            marker: { colors: [] },
                            domain: { x: [0.8, 1], y: [0, 1] },
                        }
                        for (let sales of sales_data) {
                            let customer = sales.customer
                            customer = this.get_short_name(customer)
                            if (!Object.prototype.hasOwnProperty.call(data_dict, customer)) {
                                data_dict[customer] = {
                                    type: 'bar',
                                    name: customer,
                                    legendgroup: customer,
                                    marker: { color: this.color_dict[customer] },
                                    x: [],
                                    y: [],
                                }
                            }
                            if (sales.period == this_week) {
                                data_this_week.values.push(sales.pos)
                                data_this_week.labels.push(customer)
                                data_this_week.marker.colors.push(this.color_dict[customer])
                            }

                            // Revenue (sales price)
                            data_dict[customer].x.push(sales.period)
                            data_dict[customer].y.push(sales.pos)
                        }
                        for (let trace in data_dict) {
                            data.push(data_dict[trace])
                        }
                        data.push(data_this_week)
                    }
                }
                return data
            },
            plotDataProfit() {
                let data_dict = {}
                let data = []
                if (
                    Object.prototype.hasOwnProperty.call(
                        this.dashboardSalesChartStore.plotDataRaw,
                        'sales'
                    )
                ) {
                    let sales_data = this.dashboardSalesChartStore.plotDataRaw.sales
                    if (sales_data.length > 0) {
                        let df = new DataFrame(sales_data)
                        let periods = df.unique('period').toDict().period
                        periods.sort()
                        let this_week = periods.slice(-1)
                        var data_this_week = {
                            type: 'pie',
                            name: 'This Week',
                            values: [],
                            labels: [],
                            textinfo: 'label+value',
                            xaxis: 'x2',
                            yaxis: 'y2',
                            textposition: 'inside',
                            hole: 0.4,
                            marker: { colors: [] },
                            domain: { x: [0.8, 1], y: [0, 1] },
                        }
                        for (let sales of sales_data) {
                            let customer = sales.customer
                            customer = this.get_short_name(customer)
                            if (!Object.prototype.hasOwnProperty.call(data_dict, customer)) {
                                data_dict[customer] = {
                                    type: 'bar',
                                    name: customer,
                                    legendgroup: customer,
                                    marker: { color: this.color_dict[customer] },
                                    x: [],
                                    y: [],
                                    hovertemplate: '%{y:$.4s}',
                                }
                            }
                            if (sales.period == this_week) {
                                data_this_week.values.push(sales.profit)
                                data_this_week.labels.push(customer)
                                data_this_week.marker.colors.push(this.color_dict[customer])
                            }

                            // Revenue (sales price)
                            data_dict[customer].x.push(sales.period)
                            data_dict[customer].y.push(sales.profit)
                        }
                        for (let trace in data_dict) {
                            data.push(data_dict[trace])
                        }
                        data.push(data_this_week)
                    }
                }
                return data
            },
            plotDataRevenue() {
                let data_dict = {}
                let data = []
                if (
                    Object.prototype.hasOwnProperty.call(
                        this.dashboardSalesChartStore.plotDataRaw,
                        'sales'
                    )
                ) {
                    let sales_data = this.dashboardSalesChartStore.plotDataRaw.sales
                    if (sales_data.length > 0) {
                        let df = new DataFrame(sales_data)
                        let periods = df.unique('period').toDict().period
                        periods.sort()
                        let this_week = periods.slice(-1)
                        var data_this_week = {
                            type: 'pie',
                            name: 'This Week',
                            values: [],
                            labels: [],
                            textinfo: 'label+value',
                            xaxis: 'x2',
                            yaxis: 'y2',
                            textposition: 'inside',
                            hole: 0.4,
                            marker: { colors: [] },
                            domain: { x: [0.8, 1], y: [0, 1] },
                        }
                        for (let sales of sales_data) {
                            let customer = sales.customer
                            customer = this.get_short_name(customer)
                            if (!Object.prototype.hasOwnProperty.call(data_dict, customer)) {
                                data_dict[customer] = {
                                    type: 'bar',
                                    name: customer,
                                    legendgroup: customer,
                                    marker: { color: this.color_dict[customer] },
                                    x: [],
                                    y: [],
                                    hovertemplate: '%{y:$.4s}',
                                }
                            }
                            if (sales.period == this_week) {
                                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                                data_this_week.values.push(sales.price)
                                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                                data_this_week.labels.push(customer)
                                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                                data_this_week.marker.colors.push(this.color_dict[customer])
                            }

                            // Revenue (sales price)
                            data_dict[customer].x.push(sales.period)
                            data_dict[customer].y.push(sales.price)
                        }
                        for (let trace in data_dict) {
                            data.push(data_dict[trace])
                        }
                        data.push(data_this_week)
                    }
                }
                return data
            },
            plotDataBarLine() {
                let data = [
                    { type: 'bar', name: 'Revenue', x: [], y: [], hovertemplate: '%{y:$.4s}' },
                    { type: 'bar', name: 'Cost', x: [], y: [], hovertemplate: '%{y:$.4s}' },
                    { type: 'bar', name: 'Profit', x: [], y: [], hovertemplate: '%{y:$.4s}' },
                    {
                        type: 'scatter',
                        name: 'Margin',
                        yaxis: 'y2',
                        x: [],
                        y: [],
                        line: { shape: 'spline' },
                    },
                ]
                if (
                    Object.prototype.hasOwnProperty.call(
                        this.dashboardSalesChartStore.plotDataRaw,
                        'sales'
                    )
                ) {
                    let sales_data = this.dashboardSalesChartStore.plotDataRaw.sales
                    let df = new DataFrame(sales_data)
                    let groups = df.groupBy('period')
                    let df_agg = groups.aggregate((group) => [
                        group.stat.sum('price'),
                        group.stat.sum('cost'),
                        group.stat.sum('profit'),
                        group.stat.sum('pos'),
                        group.stat.sum('invoices'),
                    ])
                    df_agg = df_agg.chain(
                        (row) => row.set('price', row.get('aggregation')[0]),
                        (row) => row.set('cost', row.get('aggregation')[1]),
                        (row) => row.set('profit', row.get('aggregation')[2]),
                        (row) => row.set('pos', row.get('aggregation')[3]),
                        (row) => row.set('invoices', row.get('aggregation')[4]),
                        (row) => row.set('margin', row.get('profit') / row.get('price'))
                    )
                    let agg_data = df_agg.toCollection()
                    for (let sales of agg_data) {
                        // Revenue (sales price)
                        data[0].x.push(sales.period)
                        data[0].y.push(sales.price)
                        // Cost
                        data[1].x.push(sales.period)
                        data[1].y.push(sales.cost)
                        // Profit
                        data[2].x.push(sales.period)
                        data[2].y.push(sales.profit)
                        // Margin
                        data[3].x.push(sales.period)
                        data[3].y.push(sales.margin)
                    }
                }
                return data
            },
            plotData() {
                let data = [
                    { type: 'scatter', name: 'Sales', x: [], y: [], line: { shape: 'spline' } },
                    {
                        type: 'scatter',
                        name: 'POs',
                        yaxis: 'y2',
                        x: [],
                        y: [],
                        line: { shape: 'spline' },
                    },
                    {
                        type: 'scatter',
                        name: 'Invoices',
                        yaxis: 'y2',
                        x: [],
                        y: [],
                        line: { shape: 'spline' },
                    },
                ]
                if (
                    Object.prototype.hasOwnProperty.call(
                        this.dashboardSalesChartStore.plotDataRaw,
                        'sales'
                    )
                ) {
                    let sales_data = this.dashboardSalesChartStore.plotDataRaw.sales
                    let df = new DataFrame(sales_data)
                    let groups = df.groupBy('period')
                    let df_agg = groups.aggregate((group) => [
                        group.stat.sum('price'),
                        group.stat.sum('cost'),
                        group.stat.sum('profit'),
                        group.stat.sum('pos'),
                        group.stat.sum('invoices'),
                    ])
                    df_agg = df_agg.chain(
                        (row) => row.set('price', row.get('aggregation')[0]),
                        (row) => row.set('cost', row.get('aggregation')[1]),
                        (row) => row.set('profit', row.get('aggregation')[2]),
                        (row) => row.set('pos', row.get('aggregation')[3]),
                        (row) => row.set('invoices', row.get('aggregation')[4]),
                        (row) => row.set('margin', row.get('profit') / row.get('price'))
                    )
                    let agg_data = df_agg.toCollection()
                    for (let sales of agg_data) {
                        // Sales
                        data[0].x.push(sales.period)
                        data[0].y.push(sales.price)
                        // POs
                        data[1].x.push(sales.period)
                        data[1].y.push(sales.pos)
                        // Invoices
                        data[2].x.push(sales.period)
                        data[2].y.push(sales.invoices)
                    }
                }
                return data
            },
        },
        data: function () {
            return {
                // SUMMARY CARDS
                to_date: true,
                filter_customer: '',
                filter_user: '',
                // CHARTS
                figureKey_main: 0,
                figureKey_BarLine: 0,
                figureKey_Revenue: 0,
                figureKey_Profit: 0,
                figureKey_PO: 0,
                period: 'week',
                period_last_filtered: 'week',
                date_range: 'this_quarter',
                range: [moment().startOf('quarter').toDate(), moment().endOf('quarter').toDate()],
                color_list: [
                    '#1f77b4', // muted blue
                    '#ff7f0e', // safety orange
                    '#2ca02c', // cooked asparagus green
                    '#d62728', // brick red
                    '#9467bd', // muted purple
                    '#8c564b', // chestnut brown
                    '#e377c2', // raspberry yogurt pink
                    '#7f7f7f', // middle gray
                    '#bcbd22', // curry yellow-green
                    '#17becf', // blue-teal
                ],
                layout_po: {
                    yaxis: {
                        title: {
                            text: 'POs',
                            font: {
                                size: 18,
                            },
                        },
                        rangemode: 'tozero',
                        anchor: 'x1',
                    },
                    xaxis: {
                        domain: [0, 0.78],
                        ancor: 'y1',
                    },
                    yaxis2: { anchor: 'x2' },
                    xaxis2: { domain: [0.8, 1], anchor: 'y2' },
                    barmode: 'stack',
                },
                layout_profit: {
                    yaxis: {
                        title: {
                            text: 'Profit',
                            font: {
                                size: 18,
                            },
                        },
                        rangemode: 'tozero',
                        anchor: 'x1',
                    },
                    xaxis: {
                        domain: [0, 0.78],
                        ancor: 'y1',
                    },
                    yaxis2: { anchor: 'x2' },
                    xaxis2: { domain: [0.8, 1], anchor: 'y2' },
                    barmode: 'stack',
                },
                layout_revenue: {
                    yaxis: {
                        title: {
                            text: 'Sales',
                            font: {
                                size: 18,
                            },
                        },
                        rangemode: 'tozero',
                        anchor: 'x1',
                    },
                    xaxis: {
                        domain: [0, 0.78],
                        ancor: 'y1',
                    },
                    yaxis2: { anchor: 'x2' },
                    xaxis2: { domain: [0.8, 1], anchor: 'y2' },
                    barmode: 'stack',
                },
                layout_barline: {
                    legend: {
                        x: 0,
                        y: 1.3,
                    },
                    yaxis: {
                        title: {
                            text: 'Sales',
                            font: {
                                size: 18,
                                color: '#1f77b4',
                            },
                        },
                        rangemode: 'tozero',
                    },
                    yaxis2: {
                        title: {
                            text: 'Margin',
                            font: {
                                size: 18,
                                color: 'rgb(214, 39, 40)',
                            },
                        },
                        rangemode: 'tozero',
                        tickformat: ',.0%',
                        overlaying: 'y',
                        side: 'right',
                    },
                    barmode: 'group',
                },
                layout: {
                    legend: {
                        x: 0,
                        y: 1.3,
                    },
                    yaxis: {
                        title: {
                            text: 'Sales',
                            font: {
                                size: 18,
                                color: '#1f77b4',
                            },
                        },
                        rangemode: 'tozero',
                    },
                    yaxis2: {
                        title: {
                            text: 'POs and Invoices',
                            font: {
                                size: 18,
                            },
                        },
                        rangemode: 'tozero',
                        overlaying: 'y',
                        side: 'right',
                    },
                },
                options: {
                    displayModeBar: false,
                    responsive: true,
                },
            }
        },
        mounted: function () {
            this.loadChart()
            this.loadSummary()
        },
        watch: {
            plotData: function () {
                // Change key to force the component to re-render
                // otherwise it will have height=0
                this.figureKey_main += 1
            },
            plotDataBarLine: function () {
                // Change key to force the component to re-render
                // otherwise it will have height=0
                this.figureKey_BarLine += 1
            },
            plotDataRevenue: function () {
                // Change key to force the component to re-render
                // otherwise it will have height=0
                this.figureKey_Revenue += 1
            },
            plotDataProfit: function () {
                // Change key to force the component to re-render
                // otherwise it will have height=0
                this.figureKey_Profit += 1
            },
            plotDataPO: function () {
                // Change key to force the component to re-render
                // otherwise it will have height=0
                this.figureKey_PO += 1
            },
        },
        methods: {
            getDates,

            get_short_name(name) {
                if (name.length > 12) {
                    name = name.slice(0, 12)
                }
                return name
            },
            plot_click(click_data) {
                let date = click_data.points[0].x
                let period = this.period_last_filtered

                let dt = moment.utc(date)
                let dt2 = moment.utc(dt).endOf(period)
                this.loadDetail(dt.toISOString(), dt2.toISOString())
            },
            reset() {
                this.dashboardSalesChartStore.setQueryDataRaw([])
                this.dashboardSalesChartStore.setPlotDataRaw([])
                this.period = 'week'
                ;((this.period_last_filtered = 'week'), (this.date_range = 'this_quarter'))
                this.range = [
                    moment().startOf('quarter').toDate(),
                    moment().endOf('quarter').toDate(),
                ]
            },
            change_date(date_range) {
                let range = this.getDates(date_range)
                if (range) {
                    this.range = range
                }
            },
            set_searching(is_searching) {
                this.searchStore.set_ajax_searching(is_searching)
            },
            filter_chart_records() {
                this.loadChart()
            },
            filter_summary_records() {
                this.loadSummary()
            },
            async loadSummary() {
                this.set_searching(true)
                try {
                    await this.dashboardSalesChartStore.fetchSalesSummary({
                        customer: this.filter_customer,
                        user: this.filter_user,
                        to_date: this.to_date,
                    })
                } catch (error) {
                    console.error('Failed to load sales summary', error)
                } finally {
                    this.set_searching(false)
                }
            },
            async loadChart() {
                this.dashboardSalesChartStore.setQueryDataRaw([])
                this.dashboardSalesChartStore.setPlotDataRaw([])
                this.period_last_filtered = this.period
                const begin_date = moment.utc(this.range[0]).format('YYYY-MM-DD')
                const end_date = moment.utc(this.range[1]).endOf('day').format('YYYY-MM-DD')
                this.set_searching(true)
                try {
                    await this.dashboardSalesChartStore.fetchSalesChart({
                        begin_date,
                        end_date,
                        period: this.period,
                    })
                } catch (error) {
                    console.error('Failed to load sales chart', error)
                } finally {
                    this.set_searching(false)
                }
            },
            async loadDetail(begin_date, end_date) {
                this.set_searching(true)
                try {
                    await this.dashboardSalesChartStore.fetchSalesDetail({ begin_date, end_date })
                } catch (error) {
                    console.error('Failed to load sales detail', error)
                } finally {
                    this.set_searching(false)
                }
            },
        },
    }
</script>
